import {
    AppError,
    AppErrorType,
} from "@sr-partner-portal/common";
import ky, {
    HTTPError,
    type Input,
    type Options,
    type ResponsePromise,
} from "ky";

class ApiHttpService {
    #kyInstance: ReturnType<typeof ky.create>;

    constructor() {
        this.#kyInstance = ky.create();
    }

    async #doAsyncRequestAsync(requestAsync) {
        let response: ResponsePromise;
        try {
            response = await requestAsync();
        } catch (error) {
            console.error(error);
            if (!(error instanceof HTTPError)) {
                // Maybe ky TimeoutError
                throw new AppError({
                    type: AppErrorType.NETWORK,
                    originalError: error,
                });
            }

            // ky 2 reads the error body itself; `data` holds the parsed JSON.
            const data = error.data;
            if (data === null || typeof data !== "object") {
                // No JSON error body (e.g. a proxy error page).
                throw error;
            }

            throw new AppError({
                type: data.type,
                details: data.details,
                originalError: error,
            });
        }

        const data = await response.json();
        return data;
    }

    async postAsync(url: Input, body: Options["json"]) {
        return await this.#doAsyncRequestAsync(async () =>
            this.#kyInstance.post(url, { json: body }),
        );
    }

    async postFormDataAsync(url: Input, body: Options["body"]) {
        return await this.#doAsyncRequestAsync(async () =>
            this.#kyInstance.post(url, { body }),
        );
    }

    async getAsync(url: Input, body: Options["json"]) {
        return await this.#doAsyncRequestAsync(async () =>
            this.#kyInstance.get(url, { json: body }),
        );
    }

    async putAsync(url: Input, body: Options["json"]) {
        return await this.#doAsyncRequestAsync(async () =>
            this.#kyInstance.put(url, { json: body }),
        );
    }

    async putFormDataAsync(url: Input, body: Options["body"]) {
        return await this.#doAsyncRequestAsync(async () =>
            this.#kyInstance.put(url, { body }),
        );
    }

    async patchAsync(url: Input, body: Options["json"]) {
        return await this.#doAsyncRequestAsync(async () =>
            this.#kyInstance.patch(url, { json: body }),
        );
    }

    async removeAsync(url, body) {
        return await this.#doAsyncRequestAsync(async () =>
            this.#kyInstance.delete(url, { json: body }),
        );
    }
}

export { ApiHttpService };
